import { z } from 'zod';
import { idParamSchema, optionalString, paginationQuerySchema, requiredString } from './common';

/**
 * A standard test procedure — the lab's SOP for one test or one analyte.
 *
 * Almost every field is optional. An SOP that has to be complete before it can
 * be saved is one the bench writes in Word instead, which is where these
 * currently live.
 */

const paragraph = () => z.string().trim().max(2000).nullish();

export const createTestProcedureSchema = z
  .object({
    title: requiredString('Title'),
    test_id: idParamSchema.nullish(),
    parameter_id: idParamSchema.nullish(),

    method: optionalString(),
    principle: paragraph(),
    specimen: optionalString(),
    container: optionalString(),
    sample_volume: optionalString(),
    storage: optionalString(),
    interferences: paragraph(),

    /**
     * The numbered steps, in order.
     *
     * Reagent volumes belong in a `ReagentRecipe`, not here — "400 µL R1 +
     * 100 µL R2" is stock and is deducted; "500 µL WR + 10 µL sample" is
     * procedure and is not. The form says so.
     */
    procedure_steps: z.array(z.string().trim().max(500)).max(40).default([]),

    incubation: optionalString(),
    wavelength: optionalString(),
    temperature: optionalString(),
    calculation: paragraph(),
    references: paragraph(),
  })
  // Mirrors the `num_nonnulls(...) = 1` CHECK, so the operator gets a field
  // error rather than a 500 from a constraint violation.
  .refine(
    (value) => (value.test_id == null) !== (value.parameter_id == null),
    'Attach the procedure to a lab test or to a parameter, not both',
  );
export type CreateTestProcedureInput = z.infer<typeof createTestProcedureSchema>;
export const updateTestProcedureSchema = createTestProcedureSchema;
export type UpdateTestProcedureInput = z.infer<typeof updateTestProcedureSchema>;

export const testProcedureQuerySchema = paginationQuerySchema.extend({
  test_id: idParamSchema.optional(),
  parameter_id: idParamSchema.optional(),
});
export type TestProcedureQuery = z.infer<typeof testProcedureQuerySchema>;
